<template>
    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
      >
      <strong class="font-bold">Error! </strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>
    <div class="p-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-6">
        <h1 id="total_count" class="text-3xl font-bold text-gray-900 dark:text-white">
            Update Queues - {{ filteredUpdateQueues.length }}
          <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
        </h1>
        <button
          type="button"
          class="flex items-center gap-2 animate-shake hover:bg-gray-100 dark:hover:bg-gray-800 bg-transparent px-3 py-2 rounded-lg transition-colors"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <path d="M12 17h.01"/>
          </svg>
          <span class="text-sm font-medium dark:text-gray-50 text-gray-900">Tutorial</span>
        </button>
      </div>
  
      <!-- Search and Filter Section -->
      <div
        class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
      >
        <!-- Search Bar -->
        <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
          <SearchBar
            v-model="searchQuery"
            placeholderText="Search updatequeues by status..."
          />
        </div>

        <div class="mb-4 md:hidden">
          <button
            @click="showFilters = !showFilters"
            class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
          >
            {{ showFilters ? "Hide Filters" : "Show Filters" }}
          </button>
        </div>
        
        <!-- Filters Section -->
        <div
          :class="[
            'md:flex md:items-center ',
            showFilters ? 'block' : 'hidden md:block',
          ]"
        >
          <FiltersSection
            :filters="dynamicFilters"
            v-model:selectedFilters="selectedFilters"
            :showFilters="showFilters"
            @update:showFilters="showFilters = $event"
            @applyFilters="applyFilters"
          />
        </div>
  
        <!-- Bulk Action Section -->
        <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
          <GlassSelect
            id="bulkactions"
            v-model="bulkAction"
            :options="[]"
            placeholder="Bulk Actions"
            :valueKey="'value'"
            :labelKey="'label'"
          />
          <button
            id="apply"
            @click="applyBulkAction"
            :disabled="!bulkAction || selectedUpdateQueues.length === 0"
            :class="[
              'btn focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200',
              bulkAction && selectedUpdateQueues.length
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed',
            ]"
          >
            Apply
          </button>
        </div>
      </div>
  
      
      <GenericTable
        ref="table"
        :columns="columns"
        :items="paginatedUpdateQueues"
        itemKey="id"
        :selectable="true"
        @update:selectedItems="updateSelectedUpdateQueues"
        @rowClick="goToUpdateQueueDetail"
      >
        <template #activity_at="{ value }">
          {{ formatDateTime(value) }}
        </template>
      </GenericTable>
  
      <!-- Pagination -->
      <div
        class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
      >
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(-1)"
            :disabled="currentPage === 1"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Previous
          </button>
          <button
            @click="changePage(1)"
            :disabled="currentPage === totalPages"
            class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
          >
            Next
          </button>
        </div>
        <div class="text-gray-700 dark:text-white">
          Page {{ currentPage }} of {{ totalPages }}
        </div>
        <div>
          <GlassSelect
            v-model="itemsPerPage"
            :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
            :valueKey="'value'"
            :labelKey="'label'"
            placeholder="Items per page"
            @change="updatePagination"
          />
        </div>
      </div>
  
      <!-- Delete Modal -->
      <div v-if="deletingItem">
        <div 
          @click.self="deletingItem=false"  
          class="items-center justify-center inset-0 absolute bg-gray-600 bg-opacity-50 flex">
          <div class="dark:bg-gray-900 bg-white p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 w-[60%] h-[45%] m-auto shadow-xl items-center text-center border-gray">
            <div class="mb-4 text-base flex space-x-2 justify-between">
              <span class="font-semibold">Are you sure you want to delete the following?</span>
              <button
                @click="deletingItem=false"
                class="hover:bg-white dark:hover:bg-gray-900"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-x-icon lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <div class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded">
              <p class="font-semibold mt-2"> All of the following related items will be deleted:</p>
              <div class="my-3" v-for="item in componetObjects">
                <div v-for="object in item.values"> 
                  <div v-if="object.value !== 0" class="font-medium justify-left flex pl-4">
                    <span class="font-medium"> {{ object.name }}: {{ object.value }}</span> 
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-center space-x-4">
              <div class="text-base flex space-x-2 justify-between">
                <button 
                  class="rounded-md text-gray-500 dark:text-white dark:hover:bg-gray-900 hover:text-red-500 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                  @click="confirmDelete">
                  <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  stroke-width="2" 
                  stroke-linecap="round" 
                  stroke-linejoin="round" 
                  class="lucide lucide-trash2-icon lucide-trash-2">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                  <line x1="10" x2="10" y1="11" y2="17"/>
                  <line x1="14" x2="14" y1="11" y2="17"/>
                  </svg>
                </button>
              </div>
              <div class=" text-base flex space-x-2 justify-between">
                <button 
                  class="bg-blue-600 text-white px-6 py-1 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
                  @click="deletingItem=false">
                  Cancel
                </button>
              </div>
            </div>
          </div>  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { getUpdateQueues } from "@/services/updateQueueService";
  import { getOrgs } from "@/services/wsOrgService";
  import { getAgents } from "@/services/agentService";
  import SearchBar from "@/components/SearchBar.vue";
  import GenericTable from "@/components/GenericTable.vue";
  import { useStore } from "@/store";
  import FiltersSection from "@/components/FiltersSection.vue";
  import { useTutorial } from "@/composables/tutorial";
  import GlassSelect from "@/components/GlassSelect.vue";

  export default {
    name: "UpdateQueueView",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      SearchBar,
      GenericTable,
      FiltersSection,
      GlassSelect,
    },
    data() {
      return {
        showFilters: false,
        updatequeues: [],
        searchQuery: "",
        filteredUpdateQueues: [],
        currentPage: 1,
        itemsPerPage: 10,
        selectedUpdateQueues: [],
        deletingItem: false,
        bulkAction: "",
        columns: [
            { key: "status_label", label: "Status", sortable: true, width: "25%" },
            { key: "agent_organization_name", label: "Hostname", sortable: true, width: "25%" },
            { key: "activity_at", label: "Last Activity", sortable: true, width: "25%" },
        ],
        error: "",
        organizations: [],
        agents: [],
        statuses : [
            { key: 'queued', label: 'Queued'},
            { key: 'in_progress', label: 'In Progress'},
            { key: 'completed', label: 'Completed'},
            { key: 'failed', label:'Failed'},
        ],
        dynamicFilters: [],
        selectedFilters: {
          organizationFilter: "",
        },
        steps: [
          {
            element: '#total_count',
            action: 'display',
            value: '',
            message: 'Total count of items in the table.'
          },
          {
            element: '#searchbar',
            action: 'display',
            value: '',
            message: 'Search and filter update queue items.'
          },
          {
            element: '#table',
            action: 'display',
            value: '',
            message: 'Update Queue overview table displaying status and hostname.'
          },
          {
            element: '#bulkactions',
            action: 'display',
            value: 1,
            message: 'Select one or more items to enable bulk actions.'
          },
          {
            element: '#apply',
            action: 'display',
            message: 'Apply bulk actions (e.g., delete, archive, modify).'
          },
        ],
      };
    },
    computed: {
      user() {
        return useStore.getUser();
      }, 
      isFiltered() {
        return (
          this.searchQuery !== "" ||
          this.selectedFilters.organizationFilter !== ""
        );
      },
      totalPages() {
        return this.itemsPerPage === -1
          ? 1
          : Math.ceil(this.filteredUpdateQueues.length / this.itemsPerPage);
      },
      paginatedUpdateQueues() {
        if (this.itemsPerPage === -1) {
          return this.filteredUpdateQueues;
        }
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        return this.filteredUpdateQueues.slice(start, end);
      },
      componetObjects() {
        return this.selectedUpdateQueues.map(updatequeue => {
          return {
            values: [
              { name: " Update Queue", value: `${updatequeue.status}-${updatequeue.agent_organization_name}`  },
            ]
          }
        })
      },
    },
    watch: {
      searchQuery() {
        this.applyFilters();
      },
    },
    async mounted() {
      await this.fetchOrganizations();
      await this.fetchAgents();
      await this.fetchUpdateQueues();
      this.initializeFilters();
    },
    methods: {
      async startTutorial() {
        await this._start(this.steps);
      },
      async fetchAgents(){
        try {
          this.agents = await getAgents();
        } catch (error) {
          console.error("Error fetching agents:", error);
        }
      },  
      async fetchOrganizations(){
        try {
          this.organizations = await getOrgs();
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      },
      async fetchUpdateQueues() {
        try {
            this.updatequeues = await getUpdateQueues();


            this.updatequeues.forEach(updatequeue => {
              updatequeue.agent_organization_name = this.agents.find(item => item.licensekey === updatequeue.agent)?.hostname;
              const statusObj = this.statuses.find(s => s.key === updatequeue.status);
              updatequeue.status_label = statusObj ? statusObj.label : updatequeue.status;

              // status-aware "last activity" timestamp
              if (updatequeue.status === "queued") {
                updatequeue.activity_at = updatequeue.queued_at;
              } else if (updatequeue.status === "in_progress") {
                updatequeue.activity_at = updatequeue.started_at || updatequeue.queued_at;
              } else {
                updatequeue.activity_at =
                  updatequeue.completed_at || updatequeue.started_at || updatequeue.queued_at;
              }
            });

          this.applyFilters();
        } catch (error) {
          console.error("Error fetching updatequeues:", error);
          if (error.response) {
            switch (error.response.status) {
              case 404:
                this.error = error.response.status +" The resource was not found. Please check the API endpoint."
                break;
              case 401:
                this.error = error.response.status + " Unauthorized access. Please check your authentication token or log in again."
                break;
              case 403:
                this.error = error.response.status + " You don't have permission to access this resource."
                            + " Contact a staff member to assign you the permissions to access this page. ";
                break;
              default:
                this.error = error.response.status =   error.response.data.detail || "Unknown error"
            }
          } else if (error.request) {
            alert(
              "No response received from the server. Please check your internet connection."
            );
          } else {
            alert(`An error occurred: ${error.message}`);
          }
        }
      },
      goToUpdateQueueDetail(updatequeue) {
        this.$router.push({
          name: "UpdateQueueDetails",
          params: { id: updatequeue.id },
        });
      },
      updateSelectedUpdateQueues(selectedItems) {
        this.selectedUpdateQueues = selectedItems;
      },
      async applyBulkAction() {
        if (this.bulkAction === "delete" ) {
          this.deletingItem = true;
        }
        this.bulkAction = "";
      },
      async confirmDelete() {
        await this.deleteSelectedUpdateQueues();
        this.deletingItem = false; 
        this.bulkAction = "";
      },
      async deleteSelectedUpdateQueues() {
        if (this.selectedUpdateQueues.length === 0) {
          alert("No updatequeues selected for deletion");
          return;
        }
        try {
          const deletePromises = this.selectedUpdateQueues.map((updatequeue) =>
            deleteUpdateQueue(updatequeue.id)
          );
          await Promise.all(deletePromises);
  
          this.updatequeues = this.updatequeues.filter(
            (updatequeue) => !this.selectedUpdateQueues.some((item) => item.licensekey === updatequeue.licensekey)
          );
          this.$refs.table.clearSelection();
          this.applyFilters();
          this.selectedUpdateQueues = [];
        } catch (error) {
          console.error("Error during bulk delete:", error);
          alert(
            "An error occurred while deleting organizations. Please try again."
          );
        }
      },
      formatDateTime(dateString) {  
        const options = {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          timeZoneName: "short",
        };
        return new Date(dateString).toLocaleString(undefined, options);
      },
      applyFilters() {
        this.filteredUpdateQueues = this.updatequeues
        this.currentPage = 1;
      },
      applyFilters() {
        this.filteredUpdateQueues = this.updatequeues.filter(
          (updatequeue) => {
            const matchesStatus = updatequeue?.status?.toLowerCase().includes(this.searchQuery.toLowerCase())
            const matchesOrgName =  updatequeue?.agent_organization_name?.toLowerCase().includes(this.searchQuery.toLowerCase()) 
            const matchesOrg = (this.selectedFilters.organizationFilter === "" ||
            updatequeue.organization === this.selectedFilters.organizationFilter);

          return (matchesStatus || matchesOrgName) && matchesOrg
          });
        this.currentPage = 1;
      },
      changePage(delta) {
        this.currentPage += delta;
      },
      updatePagination() {
        this.currentPage = 1;
      },
      initializeFilters() {
        this.dynamicFilters = [
          {
            name: "organizationFilter",
            label: "Organisation",
            options: [{ value: "", label: "All" }].concat(
              Object.keys(this.organizations).map((orgId) => ({
                value: this.organizations[orgId]?.id,
                label: this.organizations[orgId]?.name,
              }))
            ),
          },
        ];
      },
    },
  };
  </script>
  